import createFetchClient, { type Middleware } from 'openapi-fetch'
import useAuthStore from '../stores/authStore.js'
import { refreshToken, tokenNeedsRefresh } from '@/composables/useAuth.js'
import { app } from '../main.js'
import { formatRFC7231 } from 'date-fns'
import { isObject } from '@vueuse/core'
import type { allPaths } from '@/types/_general'
import {
  type EveItemErrorResponseBody,
  isEveSingleErrorResponseBody,
  type EveListErrorResponseBody,
  isEveListErrorResponseBody,
} from '@/types/_general'

function dateReplacer(this: Record<string, unknown>, key: string, value: unknown) {
  if (this[key] instanceof Date) {
    return formatRFC7231(this[key])
  }
  return value
}

export const apiFetch = createFetchClient<allPaths, 'application/json'>({
  baseUrl: import.meta.env.VITE_APP_API_ROOT,
  bodySerializer: (body: unknown) => (body instanceof FormData ? body : JSON.stringify(body, dateReplacer)),
  querySerializer: (query) => {
    const { where, projection, aggregate, ...params } = query
    const sp = new URLSearchParams(params as Record<string, string>)

    if (where != null) {
      sp.append('where', typeof where === 'string' ? where : JSON.stringify(where, dateReplacer))
    }
    if (projection != null) {
      sp.append('projection', typeof projection === 'string' ? projection : JSON.stringify(projection))
    }
    if (aggregate != null) {
      sp.append('aggregate', typeof aggregate === 'string' ? aggregate : JSON.stringify(aggregate))
    }

    return sp.toString()
  },
})

const cacheMiddleware: Middleware = {
  onRequest({ request }) {
    if (request.headers.get('Cache-Control') == null) {
      request.headers.set('Cache-Control', 'no-cache')
    }
    return request
  },
}
apiFetch.use(cacheMiddleware)

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    return await app.runWithContext(async () => {
      const authStore = useAuthStore()

      if (authStore.selectedToken != null && tokenNeedsRefresh(authStore.selectedToken.payload)) {
        await refreshToken()
      }
      // Re-read: the refresh may have replaced the token — or cleared it (server verdict). Without a token
      // the request goes out without the header and gets an ordinary 401 (before: TypeError on `.token`).
      const token = authStore.selectedToken?.token
      if (token != null) request.headers.set('x-api-key', token)
      return request
    })
  },

  async onResponse({ request, response, options }) {
    if (response.status !== 401) return response

    const token = await refreshToken()
    // null: the refresh was rejected (refreshToken cleared the tokens; useAuthEffects takes the tab to the
    // login page) or could not be made (tokens kept) — either way the caller gets the original 401. This
    // middleware never resets tokens. A new token retries the request ONCE, GET only: a used body cannot be copied.
    if (token == null || request.method !== 'GET') return response

    // Raw retry: a Response returned from onResponse must have an unread body (apiFetch.request would have read
    // it), and it deliberately bypasses the middleware chain — no loop; mediaFileMiddleware only concerns uploads.
    const retryRequest = new Request(request)
    retryRequest.headers.set('x-api-key', token)
    // Called unbound: the browser's fetch invoked as a method of the plain `options` object throws
    // "Illegal invocation" (its `this` must be the window or undefined).
    const { fetch: rawFetch } = options
    return await rawFetch(retryRequest)
  },
}
apiFetch.use(authMiddleware)

const requestFilesMap = new WeakMap<Request, { path: string; file: File }[]>()

function isEmptyObject(obj: unknown): boolean {
  if (!isObject(obj)) return false
  return Object.values(obj as Record<string, unknown>).every((v) => v === undefined || v === null || isEmptyObject(v))
}

function extractFilesAndClone(data: unknown, files: { path: string; file: File }[], path = ''): unknown {
  if (data instanceof File) {
    files.push({ path, file: data })
    return undefined
  } else if (Array.isArray(data)) {
    return data.map((item, index) => extractFilesAndClone(item, files, path ? `${path}.${index}` : `${index}`))
  } else if (isObject(data)) {
    const newObj: Record<string, unknown> = {}
    for (const key in data) {
      if (Object.hasOwn(data, key)) {
        const value = extractFilesAndClone(
          (data as unknown as Record<string, unknown>)[key],
          files,
          path ? `${path}.${key}` : key
        )
        // Skip empty objects (objects with only undefined/null values) - these are left over after file extraction
        if (value !== undefined && !isEmptyObject(value)) {
          newObj[key] = value
        }
      }
    }
    return newObj
  }
  return data
}

const mediaFileMiddleware: Middleware = {
  onRequest({ request, body, options }) {
    if (request.method === 'GET' || request.url.includes('/media--upload/')) return

    if (isObject(body) && !(body instanceof FormData)) {
      const files: { path: string; file: File }[] = []
      const newBody = extractFilesAndClone(body, files)

      if (files.length > 0) {
        const newRequest = new Request(request, {
          body: options.bodySerializer(newBody as Parameters<typeof options.bodySerializer>[0]),
        })
        requestFilesMap.set(newRequest, files)
        return newRequest
      }
    }
  },

  async onResponse({ response, request }) {
    if (!response.ok) return
    const files = requestFilesMap.get(request)
    // eslint-disable-next-line prefer-const
    let [, resource, resourceId] = new URL(request.url).pathname.split('/')
    resourceId ??= (await response.clone().json())._id

    if (files != null && resource != null && resourceId != null) {
      for (const { path, file } of files) {
        const body = new FormData()
        body.append('file', file)
        body.append('dotted_path', path)
        body.append('domain', resource)
        await apiFetch.request('post', '/media--upload/{reference_id}', {
          params: { path: { reference_id: resourceId } },
          // @ts-expect-error FormData _is_ in fact assignable to body!
          body,
        })
      }
      requestFilesMap.delete(request)
    }
  },
}
apiFetch.use(mediaFileMiddleware)

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  statusCode: number
  body: unknown

  constructor(message: string, statusCode: number, body: unknown, options?: ErrorOptions) {
    super(message, options)
    this.statusCode = statusCode
    this.body = body
  }
}

export class EveError extends ApiError {
  issues?: EveItemErrorResponseBody['_issues']
  localeMessage?: string

  constructor(body: EveItemErrorResponseBody, statusCode: number, options?: ErrorOptions) {
    super(
      typeof body._error === 'string' ? body._error : (body._error?.message ?? 'Unknown API Error'),
      statusCode,
      body,
      options
    )
    this.localeMessage = body._locale_message
    this.issues = body._issues
  }
}

export class EveListError extends ApiError {
  itemIssues: Record<number, EveItemErrorResponseBody['_issues']>
  localeMessage?: string

  constructor(body: EveListErrorResponseBody, statusCode: number, options?: ErrorOptions) {
    super(
      typeof body._error === 'string' ? body._error : (body._error?.message ?? 'Unknown API Error'),
      statusCode,
      body,
      options
    )
    this.localeMessage = body._locale_message
    this.itemIssues = {}
    for (const [index, item] of body._items.entries()) {
      if (item._status === 'ERR' && item._issues != null) {
        this.itemIssues[index] = item._issues
      }
    }
  }
}

const MAX_ISSUE_DEPTH = 8

/**
 * The messages in an Eve `_issues` value, which nests strings in arrays and objects like the payload.
 * Each message once, in the order found.
 */
export function issueMessages(issues: unknown): string[] {
  const collect = (value: unknown, depth: number): string[] => {
    if (depth > MAX_ISSUE_DEPTH) return []
    if (typeof value === 'string') return [value]
    if (Array.isArray(value)) return value.flatMap((item: unknown) => collect(item, depth + 1))
    if (value != null && typeof value === 'object') {
      return Object.values(value as Record<string, unknown>).flatMap((item) => collect(item, depth + 1))
    }
    return []
  }
  return [...new Set(collect(issues, 0))]
}

export function createApiError(body: unknown, statusCode: number): Error {
  if (isEveSingleErrorResponseBody(body)) {
    return new EveError(body, statusCode)
  } else if (isEveListErrorResponseBody(body)) {
    return new EveListError(body, statusCode)
  }
  if (typeof body === 'string') {
    return new ApiError(body, statusCode, body)
  }
  return new ApiError('Unknown API Error', statusCode, body)
}
